import { getStage } from "../../content/stages.js";
import { normalizeStartDeck, startDeckCardIds } from "../../features/stageEditor/startDeck.js";
import { createLcg } from "../rng/lcg.js";
import { shuffle } from "../rng/shuffle.js";
import type { CardId, CardInstance } from "../types/index.js";
import { BRING_IN_CARD_IDS } from "./bringIn.js";
import { createCardInstances } from "./createInstance.js";

const INTERN_ID: CardId = "intern";
const IDLE_ID: CardId = "idle";
const HEDGE_ID: CardId = "hedge";

const STANDARD_CARD_IDS: readonly CardId[] = [
  ...Array.from({ length: 7 }, () => INTERN_ID),
  ...Array.from({ length: 3 }, () => IDLE_ID),
];

const HARD_CARD_IDS: readonly CardId[] = [
  ...Array.from({ length: 7 }, () => INTERN_ID),
  ...Array.from({ length: 2 }, () => IDLE_ID),
  HEDGE_ID,
];

export type InitialDeckInput = {
  rngState: number;
  nextCardSequence: number;
  stageId?: string;
  /** 持ち込みカード。指定時は所与9枚（新卒7・窓際2）に1枚足す。 */
  bringInCardId?: CardId;
};

const HEDGE_STAGE_IDS = new Set(["hard", "wide", "deep", "choice", "labyrinth", "product-release", "stabilize", "foundation"]);

const BRING_IN_FIXED_CARD_IDS: readonly CardId[] = [
  ...Array.from({ length: 7 }, () => INTERN_ID),
  ...Array.from({ length: 2 }, () => IDLE_ID),
];

function customCardIds(stageId?: string, bringInCardId?: CardId): CardId[] | null {
  if (!stageId) return null;
  const custom = getStage(stageId).startingDeck;
  if (!custom) return null;
  const ids = startDeckCardIds(normalizeStartDeck(custom)) as CardId[];
  if (bringInCardId && (BRING_IN_CARD_IDS as readonly CardId[]).includes(bringInCardId)) {
    return [...ids, bringInCardId];
  }
  return ids;
}

function initialCardIds(stageId?: string, bringInCardId?: CardId): readonly CardId[] {
  const custom = customCardIds(stageId, bringInCardId);
  if (custom) return custom;
  if (bringInCardId && (BRING_IN_CARD_IDS as readonly CardId[]).includes(bringInCardId)) {
    return [...BRING_IN_FIXED_CARD_IDS, bringInCardId];
  }
  return stageId && HEDGE_STAGE_IDS.has(stageId) ? HARD_CARD_IDS : STANDARD_CARD_IDS;
}

export type InitialDeckResult = {
  deck: CardInstance[];
  rngState: number;
  nextCardSequence: number;
};

export function createInitialDeck(input: InitialDeckInput): InitialDeckResult {
  const { instances, nextCardSequence } = createCardInstances(
    { nextCardSequence: input.nextCardSequence },
    initialCardIds(input.stageId, input.bringInCardId),
  );

  const rng = createLcg(input.rngState);
  const deck = shuffle(instances, rng);

  return {
    deck,
    rngState: rng.state,
    nextCardSequence,
  };
}
